import { useState } from 'react';
import { Box, CircularProgress, Container, MenuItem, Paper, Stack, TextField } from '@mui/material';
import PageHeader from 'components/common/PageHeader/PageHeader';
import CustomText from 'components/common/CustomText/CustomText';
import CustomButton from 'components/buttons/CustomButton/CustomButton';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import { useGetStores } from 'hooks/useStore';
import { useAgentSearchStream } from 'hooks/useSearch';
import { AgentSearchDebug, AgentSearchProgress } from 'services/searchService';
import { ProductCard } from 'pages/product/SearchTestPage/SearchTestPage';

// The agent search, tried here beside the search test: the products come back
// as the app shows them, and below them every tool call it made - so a bad answer can be traced to the call that went wrong.
export default function AgentSearchTestPage() {
  const globalStyles = useGlobalStyles();
  const [input, setInput] = useState('');
  const [storeId, setStoreId] = useState('');

  // Every store fits one page of this size today.
  const { data: stores = [] } = useGetStores(
    { page: 1, size: 100 },
    { select: (res: any) => res.data, refetchOnWindowFocus: false },
  );

  const { search: run, steps, data, isLoading, isError } = useAgentSearchStream();
  const search = () => {
    if (input.trim()) run({ input: input.trim(), ...(storeId ? { storeId } : {}) });
  };

  return (
    <Box className={globalStyles.container}>
      <Container maxWidth={'xl'} className={globalStyles.container}>
        <Box pt={2}>
          <PageHeader title={'Agent search'} buttons={[]} />
        </Box>

        <Stack direction='row' spacing={2} mt={3} alignItems='center'>
          <TextField
            fullWidth
            size='small'
            placeholder='Type a search the way a shopper would, in any language'
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && search()}
          />
          <TextField select size='small' label='Store' value={storeId} onChange={(event) => setStoreId(event.target.value)} sx={{ minWidth: 220 }}>
            <MenuItem value=''>All stores (market)</MenuItem>
            {stores.map((store: any) => (
              <MenuItem key={store.id} value={store.id}>
                {store.info?.name ?? store.id}
              </MenuItem>
            ))}
          </TextField>
          <CustomButton text='Search' disabled={isLoading || !input.trim()} onClick={search} />
        </Stack>

        {/* Each call as the agent makes it, with what it looked for and found. */}
        {isLoading && (
          <Stack spacing={1} mt={4}>
            {messages(steps).map((message, index) => (
              <Stack key={index} direction='row' spacing={1} alignItems='flex-start'>
                <CustomText text='✓' color={colors.primary} fontWeight={600} />
                <CustomText text={message} />
              </Stack>
            ))}
            <Stack direction='row' spacing={1} alignItems='center'>
              <CircularProgress size={16} />
              <CustomText text={steps.length ? 'Searching…' : 'Reading your search…'} color={colors.gray} />
            </Stack>
          </Stack>
        )}

        {isError && !isLoading && (
          <Box mt={4}>
            <CustomText text='The search failed.' color={colors.error} />
          </Box>
        )}

        {data && !isLoading && (
          <>
            <Box mt={4}>
              <CustomText
                text={data.products.length === 0 ? 'No products found.' : `${data.products.length} product${data.products.length === 1 ? '' : 's'}`}
                fontWeight={600}
                fontSize={18}
              />
              <CustomText
                text={`Took ${(data.tookMs / 1000).toFixed(1)}s · searched ${data.scope} products on sale · ${data.steps.length} tool calls · $${data.costUsd.toFixed(4)} on ${data.model}`}
                fontSize={13}
                color={colors.gray}
              />
            </Box>

            <Box mt={2} display='flex' flexWrap='wrap' gap={2}>
              {data.products.map((storeProduct: any) => (
                <ProductCard key={storeProduct.id} storeProduct={storeProduct} />
              ))}
            </Box>

            <Calls data={data} />
          </>
        )}
      </Container>
    </Box>
  );
}

// The agent's lines to the shopper, each once - calls made together often
// say the same.
function messages(steps: AgentSearchProgress[]): string[] {
  const lines = steps.map((step) => step.message?.trim() ?? '').filter(Boolean);
  return lines.filter((line, index) => lines.indexOf(line) === index);
}

// Every tool call the agent made, in order, with what it asked and what it got.
function Calls({ data }: { data: AgentSearchDebug }) {
  return (
    <Paper variant='outlined' sx={{ p: 3, mt: 4, mb: 4 }}>
      <CustomText text='What the agent did' fontWeight={600} fontSize={16} />
      {data.steps.map((step, index) => (
        <Box key={index} mt={2}>
          <CustomText text={`${index + 1}. ${step.tool}`} fontWeight={600} fontSize={13} color={colors.gray} />
          <CustomText text={JSON.stringify(step.args)} fontSize={12} sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }} />
          {step.result && (
            <Box
              component='pre'
              sx={{ m: 0, mt: 0.5, p: 1, maxHeight: 160, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap', backgroundColor: colors.topBackground, borderRadius: 1 }}
            >
              {step.result}
            </Box>
          )}
        </Box>
      ))}
    </Paper>
  );
}
